import { Stack, Typography, useTheme } from "@mui/material";
import type { Control, FieldValues, Path } from "react-hook-form";
import type { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	CONTRACT_REQUIRED_MINIMUM_FIELDS,
	getContractRequiredMinimumMessage,
} from "web_myplann/Portfolio/Forms/ContractShared/contractRequiredMinimum";
import { useProducerProductOptions } from "web_myplann/Portfolio/Helpers/useProducerProductOptions";
import { RequiredMinimumMessage } from "web_myplann/Portfolio/PortfolioShared/RequiredMinimumMessage";
import { FlatRowFormCombobox } from "web_myplann/Shared/Components/FlatRow/FlatRowFormCombobox";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";

type InvestmentBasicFieldsProps<
	FormType extends FieldValues,
	TTransformed extends FieldValues | undefined,
> = {
	control: Control<FormType, unknown, TTransformed>;
	domain: ClientPortfolioDomainEnum;
	namePlaceholder: string;
};

const PageResources = Resources.Portfolio.AddInvestment;

export const InvestmentBasicFields = <
	FormType extends FieldValues,
	TTransformed extends FieldValues | undefined,
>({
	control,
	domain,
	namePlaceholder,
}: InvestmentBasicFieldsProps<FormType, TTransformed>) => {
	const theme = useTheme();
	const { t } = useResource();

	const {
		producerOptions,
		productOptions,
		isLoadingProducers,
		isLoadingProducts,
	} = useProducerProductOptions(control, domain);

	return (
		<>
			<Typography variant="sectionLabel">
				{t(PageResources.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name={"name" as Path<FormType>}
					label={t(PageResources.Fields.name)}
					placeholder={namePlaceholder}
				/>
				<FlatRowFormInput
					control={control}
					name={"contractNumber" as Path<FormType>}
					label={t(PageResources.Fields.contractNumber)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormCombobox
					control={control}
					name={"producer" as Path<FormType>}
					label={t(PageResources.Fields.producer)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProducer,
					)}
					options={producerOptions}
					isLoading={isLoadingProducers}
				/>
				<FlatRowFormCombobox
					control={control}
					name={"product" as Path<FormType>}
					label={t(PageResources.Fields.product)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.select)}
					searchPlaceholder={t(
						Resources.Portfolio.AddShared.Placeholders.searchOrEnterProduct,
					)}
					options={productOptions}
					isLoading={isLoadingProducts}
					isLast
				/>
			</Stack>
			<RequiredMinimumMessage
				control={control}
				fields={CONTRACT_REQUIRED_MINIMUM_FIELDS.map(
					(field) => field as Path<FormType>,
				)}
				message={getContractRequiredMinimumMessage(t, PageResources.Fields)}
			/>
		</>
	);
};
